/* Next Human custom sections (nh-*). No dependencies; uses Dawn's pubsub when present. */
(() => {
  const pad = (n) => String(Math.max(0, n)).padStart(2, '0');

  const formatMoney = (cents) => {
    const format = (window.Shopify && window.Shopify.money_format) || window.nhMoneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const withCommas = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toLocaleString('en-US'))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value.replace('.', ','))
      .replace(/\{\{\s*amount\s*\}\}/, withCommas);
  };

  /* ---------- Countdown ---------- */
  if (!customElements.get('nh-countdown')) {
    customElements.define(
      'nh-countdown',
      class extends HTMLElement {
        connectedCallback() {
          this.units = {
            h: this.querySelector('[data-unit="h"]'),
            m: this.querySelector('[data-unit="m"]'),
            s: this.querySelector('[data-unit="s"]'),
          };
          this.tick();
          this.timer = setInterval(() => this.tick(), 1000);
        }
        disconnectedCallback() {
          clearInterval(this.timer);
        }
        target() {
          if (this.dataset.mode === 'date' && this.dataset.end) {
            const d = new Date(this.dataset.end);
            if (!isNaN(d)) return d;
          }
          const end = new Date();
          end.setHours(24, 0, 0, 0);
          return end;
        }
        tick() {
          const diff = Math.floor((this.target() - Date.now()) / 1000);
          if (diff <= 0 && this.dataset.mode === 'date') {
            clearInterval(this.timer);
            if (this.dataset.hideExpired === 'true') {
              const bar = this.closest('.nh-countdown');
              if (bar && !(window.Shopify && window.Shopify.designMode)) bar.hidden = true;
            }
          }
          const total = Math.max(0, diff);
          this.units.h.textContent = pad(Math.floor(total / 3600));
          this.units.m.textContent = pad(Math.floor((total % 3600) / 60));
          this.units.s.textContent = pad(total % 60);
        }
      }
    );
  }

  /* ---------- Carousel (scroll-snap + arrows/dots) ---------- */
  if (!customElements.get('nh-carousel')) {
    customElements.define(
      'nh-carousel',
      class extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-track]');
          if (!this.track) return;
          this.style.setProperty('--nh-per-view', this.dataset.perView || 4);
          this.style.setProperty('--nh-per-view-m', this.dataset.perViewMobile || 1.15);
          this.prev = this.querySelector('[data-prev]');
          this.next = this.querySelector('[data-next]');
          this.dotsWrap = this.querySelector('[data-dots]');
          this.prev && this.prev.addEventListener('click', () => this.go(-1));
          this.next && this.next.addEventListener('click', () => this.go(1));
          this.track.addEventListener('scroll', () => this.update(), { passive: true });
          this.ro = new ResizeObserver(() => this.build());
          this.ro.observe(this.track);
        }
        disconnectedCallback() {
          this.ro && this.ro.disconnect();
        }
        slides() {
          return Array.from(this.track.children);
        }
        step() {
          const s = this.slides();
          if (s.length < 2) return this.track.clientWidth;
          return s[1].offsetLeft - s[0].offsetLeft;
        }
        pages() {
          const step = this.step() || 1;
          const visible = Math.max(1, Math.round(this.track.clientWidth / step));
          return Math.max(1, this.slides().length - visible + 1);
        }
        build() {
          const scrollable = this.track.scrollWidth > this.track.clientWidth + 4;
          this.classList.toggle('is-static', !scrollable);
          if (this.dotsWrap) {
            this.dotsWrap.innerHTML = '';
            const n = this.pages();
            for (let i = 0; i < n; i++) {
              const b = document.createElement('button');
              b.type = 'button';
              b.setAttribute('aria-label', `Go to slide ${i + 1}`);
              b.addEventListener('click', () => this.track.scrollTo({ left: i * this.step(), behavior: 'smooth' }));
              this.dotsWrap.appendChild(b);
            }
          }
          this.update();
        }
        go(dir) {
          this.track.scrollBy({ left: dir * this.step(), behavior: 'smooth' });
        }
        update() {
          const max = this.track.scrollWidth - this.track.clientWidth - 2;
          if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
          if (this.next) this.next.disabled = this.track.scrollLeft >= max;
          if (this.dotsWrap) {
            const i = Math.round(this.track.scrollLeft / (this.step() || 1));
            Array.from(this.dotsWrap.children).forEach((d, idx) => d.setAttribute('aria-current', idx === i));
          }
        }
      }
    );
  }

  /* ---------- Bundle offers ---------- */
  const updateButtonPrice = (sectionId, totalCents, fullCents) => {
    const scope = document.querySelector(`#ProductInfo-${sectionId}`) || document;
    const now = scope.querySelector('[data-nh-btn-now]');
    const was = scope.querySelector('[data-nh-btn-was]');
    if (now) now.textContent = formatMoney(totalCents);
    if (was) {
      was.hidden = !(fullCents > totalCents);
      was.textContent = fullCents > totalCents ? formatMoney(fullCents) : '';
    }
    const sticky = document.querySelector('[data-nh-sticky-price]');
    if (sticky) sticky.textContent = formatMoney(totalCents);
  };

  if (!customElements.get('nh-bundles')) {
    customElements.define(
      'nh-bundles',
      class extends HTMLElement {
        connectedCallback() {
          this.qty = this.querySelector('[data-bundle-qty]');
          this.addEventListener('change', (e) => {
            if (e.target.matches('input[type="radio"]')) this.select(e.target);
          });
          const checked = this.querySelector('input[type="radio"]:checked') || this.querySelector('input[type="radio"]');
          if (checked) {
            checked.checked = true;
            this.select(checked);
          }
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && !this.unsub) {
            this.unsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event));
          }
        }
        disconnectedCallback() {
          this.unsub && this.unsub();
          this.unsub = null;
        }
        select(input) {
          this.querySelectorAll('.nh-bundle').forEach((el) => el.classList.toggle('is-selected', el.contains(input)));
          if (this.qty) this.qty.value = input.value;
          updateButtonPrice(this.dataset.sectionId, Number(input.dataset.total), Number(input.dataset.full));
        }
        onVariantChange(event) {
          if (!event || !event.data || event.data.sectionId !== this.dataset.sectionId || !event.data.html) return;
          const fresh = event.data.html.getElementById(this.id);
          if (!fresh) return;
          const current = this.querySelector('input[type="radio"]:checked');
          const keep = current ? current.value : null;
          this.innerHTML = fresh.innerHTML;
          this.qty = this.querySelector('[data-bundle-qty]');
          const match = keep && this.querySelector(`input[type="radio"][value="${keep}"]`);
          const target = match || this.querySelector('input[type="radio"]');
          if (target) {
            target.checked = true;
            this.select(target);
          }
        }
      }
    );
  }

  /* ---------- Subscribe & save checkbox ---------- */
  document.addEventListener('change', (e) => {
    if (!e.target.matches('[data-nh-subscribe]')) return;
    const plan = e.target.closest('.nh-subscribe').querySelector('[data-nh-plan]');
    if (plan) plan.disabled = !e.target.checked;
  });

  /* ---------- Sticky add to cart ---------- */
  if (!customElements.get('nh-sticky-atc')) {
    customElements.define(
      'nh-sticky-atc',
      class extends HTMLElement {
        connectedCallback() {
          this.mainButton = document.querySelector('product-info .product-form__submit');
          const btn = this.querySelector('[data-nh-sticky-add]');
          btn &&
            btn.addEventListener('click', () => {
              const form = document.querySelector('product-info product-form form');
              if (form && form.requestSubmit) form.requestSubmit();
              else if (this.mainButton) this.mainButton.click();
            });
          if (!this.mainButton) return;
          this.io = new IntersectionObserver(([entry]) => {
            const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.classList.toggle('is-visible', passed);
            this.setAttribute('aria-hidden', String(!passed));
          });
          this.io.observe(this.mainButton);
        }
        disconnectedCallback() {
          this.io && this.io.disconnect();
        }
      }
    );
  }

  /* ---------- Stats: count numbers up from 0 when scrolled into view ---------- */
  const initCounters = (root = document) => {
    const els = root.querySelectorAll('[data-nh-count]:not([data-nh-count-ready])');
    if (!els.length) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const run = (el) => {
      const m = el.dataset.nhCount.match(/^(\D*)([\d.,]+)(.*)$/);
      if (!m) return;
      const prefix = m[1];
      const suffix = m[3];
      const target = parseFloat(m[2].replace(/,/g, ''));
      const decimals = (m[2].split('.')[1] || '').length;
      const commas = m[2].includes(',');
      if (reduce || isNaN(target)) return;
      const duration = 1600;
      const start = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        const value = target * eased;
        const text = commas
          ? value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
          : value.toFixed(decimals);
        el.textContent = prefix + text + suffix;
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          run(entry.target);
        });
      },
      { threshold: 0.4 }
    );
    els.forEach((el) => {
      el.setAttribute('data-nh-count-ready', '');
      const m = el.dataset.nhCount.match(/^(\D*)([\d.,]+)(.*)$/);
      if (!m || reduce) return;
      el.textContent = m[1] + '0' + m[3];
      io.observe(el);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initCounters());
  else initCounters();
  document.addEventListener('shopify:section:load', (e) => initCounters(e.target));

  /* ---------- Cart drawer: upgrade to the next quantity tier ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-nh-upgrade]');
    if (!btn) return;
    const input = document.getElementById(`Drawer-quantity-${btn.dataset.line}`);
    if (!input) return;
    btn.disabled = true;
    input.value = btn.dataset.qty;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Video cards ---------- */
  document.addEventListener('click', (e) => {
    const play = e.target.closest('[data-nh-play]');
    if (!play) return;
    const card = play.closest('.nh-video-card');
    const video = card && card.querySelector('video[data-nh-video]');
    if (!video) return;
    document.querySelectorAll('video[data-nh-video]').forEach((v) => {
      if (v !== video && !v.paused) v.pause();
    });
    video.controls = true;
    video.play();
    card.classList.add('is-playing');
    video.addEventListener('pause', () => video.ended && card.classList.remove('is-playing'), { once: true });
  });
})();
